import React from 'react';
import { useHistory, useLocation, Route, Redirect } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import reset from 'styled-reset';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { startTimer, stopTimer } from './store/actions/timer.actions';

import theme from './design-system';
import { getLocalUser } from './utils/auth';
import { fetchAuction } from './store/actions/auction.actions';
import { fetchRelatedDealers } from './store/actions/user.actions';
import NotificationWrapper from './components/shared/notification/NotificationWrapper';
import AuctionPusher from './components/shared/pusher/AuctionPusher';
import { AppProvider } from './context';
import { gtmTracker, gtmVariables } from './utils/gtmTracker';

import {
  SingleOfferModal,
  WonModal,
  LostModal,
  BuyNowModal,
  SuccessModal,
  ConfirmMakeOfferModal,
  BuyFor,
  ConfirmBuyFor,
} from './components/Modals';

import { Temperature } from './components/Temperature';
import { AcceptedModal, RejectedModal } from './components/AuctionList/modals';
import { ToastContainer } from './components/toast-notification-container';
import { saveFirstLoginOfTheDay } from './utils/freeShippingDialog';
import { checkToken } from './utils';
import TooltipFeedback from './components/Tooltips/TooltipFeedback';
import TooltipFeedbackOld from './components/Tooltips/TooltipFeedback/old';
import { FiltersContextProvider } from './context/AuctionFilters';
import { userFilterService } from './services/UserFilterService';
import AppConstants from './constants/appConstants';
import Pusher from 'pusher-js';

import { AutoBidOfferModal } from './components/Modals/AutoBid/AutomaticOffer';

import MoreOptions from './components/Modals/AutoBid/MoreOptions';
import RemoveAutomaticOffer from './components/Modals/AutoBid/RemoveAutomaticOffer';
import TiebreakerRules from './components/Modals/AutoBid/TiebreakerRules';
import ConfirmAutomaticOffer from './components/Modals/AutoBid/ConfirmAutomaticOffer';
import ConfirmBuyNowModal from './components/Modals/ConfirmBuyNowModal';

import { deauthenticate } from './store/actions/authentication.actions';
import { FlagProvider } from '@unleash/proxy-client-react';
import UnleashUserContext from './components/shared/UnleashUserContext';
import { unleashConfig } from './constants/unleashConfig';
import { FilterProvider } from './hooks/useFilter';
import { LaunchWindowsProvider } from './context/LaunchWindowsContext';
import { ConfigCatContextProvider } from './context/ConfigCatContext';

import { PreferencesModal } from './components/Modals/PreferencesModal';
import { ProductNF } from './components/Modals/ProductNF';

import balboaFont from './styles/balboa-w01-bold.ttf';
import balboaFontCondensed from './styles/balboa-condensed.otf';
import { openDealerApp } from './utils/app';
import { isEmbeddedPurchasesRoute } from './utils/embed';
import { LoggedRoutes, UnloggedRoutes } from './routes';
import { NotificationContextProvider } from './context/NotificationContext';

export const pusher = new Pusher(AppConstants.PusherKey, {
  encrypted: true,
});

const PrivateRoute = ({ children, onlyNinja, ...rest }) => {
  const user = getLocalUser();

  return (
    <Route
      {...rest}
      render={({ location }) => {
        if (!user.isLoggedIn()) {
          const currentPath = location.pathname + location.search;

          // Apenas salva o caminho para /detalhes (para voltar após login)
          // Outras rotas vão para / após login
          if (location.pathname.startsWith('/detalhes')) {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
            if (location.state) {
              sessionStorage.setItem('redirectAfterLoginState', JSON.stringify(location.state));
            }
          }
          // Para outras rotas de área logada, não salva redirectAfterLogin
          // Isso faz com que após login vá para / (padrão)

          openDealerApp();
          return <Redirect to={{ pathname: '/login', state: { from: location } }} />;
        }

        if (onlyNinja) {
          return user.isNinja() ? children : <Redirect to="/" />;
        }

        return children;
      }}
    />
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});
const GlobalStyle = createGlobalStyle`
  ${reset};
  strong {
    font-weight: 700;
  }
  p {
    margin: 0.5rem 0 1rem 0;
  }
  @font-face {
    font-family: 'Balboa W01 Cond';
    src: url(${balboaFont}) format('truetype');
  }

  @font-face {
    font-family: 'Balboa Condensed';
    src: url(${balboaFontCondensed}) format('truetype');
  }
`;
const App = () => {
  //usePageViews();
  const dispatch = useDispatch();
  const auth = useSelector(state => state.authentication);
  const user = useSelector(state => state.user);
  const auctions = useSelector(state => state.auctions);
  const notifier = React.useRef();
  const history = useHistory();
  const location = useLocation();

  const unleashConfig = {
    url:
      process.env.IC_ENV === 'production'
        ? 'https://unleash-proxy.instacarro.com/proxy'
        : 'https://proxy-unleash.instacarro.com/proxy',
    clientKey: 'secret-one',
    refreshInterval: 60,
    appName: 'lojas-web',
    environment: process.env.IC_ENV === 'production' ? 'prd' : 'dev',
  };

  React.useEffect(() => {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'optimize.activate',
      });
    }
  }, []);

  // Tell Customer.io's Gist SDK about SPA route changes so it can trigger
  // display of queued in-app messages (it fetches them fine on its own,
  // but never auto-shows them without this signal in a client-side router).
  React.useEffect(() => {
    if (window.Gist) {
      window.Gist.setCurrentRoute(location.pathname);
    }
  }, [location]);

  const isEmbeddedPurchases = isEmbeddedPurchasesRoute(
    history.location.pathname,
    history.location.search
  );

  React.useEffect(() => {
    const { user: _user } = auth;

    gtmTracker({
      [gtmVariables.USER_EMAIL]: _user.email,
      [gtmVariables.IS_NINJA]: _user.isNinja(),
      [gtmVariables.USER_DEALERSHIP]: _user.dealershipName,
      [gtmVariables.BUYER_ID]: user.buyerId,
    });

    if (_user.isLoggedIn() && window.amplitude) {
      window.amplitude.setUserId(_user.id);

      const identify = new window.amplitude.Identify();
      identify.set('email', _user.email);
      identify.set('dealershipName', _user.dealershipName);
      identify.set('isNinja', _user.isNinja());
      identify.set('dealershipId', _user.id);
      window.amplitude.identify(identify);
    }
  }, [user, auth]);

  // Start timer actions on the store.
  React.useEffect(() => {
    dispatch(startTimer());

    return () => {
      dispatch(stopTimer());
    };
  }, []);

  // async function dispatchCars() {
  //   await dispatch(fetchAuctions());
  // }

  // Fetch user related dealers
  React.useEffect(() => {
    const user = getLocalUser();

    console.log('currentPath', location.pathname + location.search);

    if (!user?.email) {
      if (isEmbeddedPurchasesRoute(location.pathname, location.search)) {
        return;
      }

      const currentPath = location.pathname + location.search;

      // Lista de rotas que não requerem login (públicas)
      const publicRoutes = [
        '/login',
        '/esqueceu-senha',
        '/resetar-senha',
        '/resetear-senha', // Suporte para variação com 'e'
        '/terms',
        '/cadastrar-lojista',
      ];

      // Verifica se é uma rota pública
      if (
        publicRoutes.some(
          route => location.pathname === route || location.pathname.startsWith(route)
        )
      ) {
        openDealerApp();
        return;
      }

      // Rotas de área logada que devem redirecionar para login
      const loggedAreaRoutes = [
        '/',
        '/detalhes',
        '/compras',
        '/favoritos',
        '/perfis',
        '/ferramenta-comercial',
        '/atualizar-perfil',
        '/preferencias',
      ];

      const isLoggedAreaRoute = loggedAreaRoutes.some(
        route => location.pathname === route || location.pathname.startsWith(route)
      );

      if (isLoggedAreaRoute) {
        // Apenas salva o caminho para /detalhes (para voltar após login)
        // Outras rotas vão para / após login
        if (location.pathname.startsWith('/detalhes')) {
          sessionStorage.setItem('redirectAfterLogin', currentPath);
          if (location.state) {
            sessionStorage.setItem('redirectAfterLoginState', JSON.stringify(location.state));
          }
        }
        // Para outras rotas de área logada, não salva redirectAfterLogin
        // Isso faz com que após login vá para / (padrão)
        openDealerApp();
        return history.push('/login');
      }
    }

    if (user?.email && user?.isNinja()) {
      checkToken()
        .then(async () => {
          dispatch(fetchRelatedDealers());
        })
        .catch(() => {
          const currentPath = history.location.pathname + history.location.search;
          if (currentPath !== '/login') {
            sessionStorage.setItem('redirectAfterLogin', currentPath);
          }
          dispatch(
            deauthenticate(() => {
              openDealerApp();
              history.push('/login');
            })
          );
        });
    }

    //dispatchCars();
    if (auctions.detailedAuction._id) {
      dispatch(fetchAuction(auctions.detailedAuction._id));
    }
  }, [auth, location, history, dispatch, auctions.detailedAuction._id]);

  React.useEffect(() => {
    openDealerApp();
    saveFirstLoginOfTheDay();
  }, []);

  return (
    <ConfigCatContextProvider>
      <NotificationContextProvider>
        <FlagProvider config={unleashConfig}>
          <UnleashUserContext />
          <FiltersContextProvider service={userFilterService} notify={console}>
            <QueryClientProvider client={queryClient}>
              <ThemeProvider theme={theme}>
                <AppProvider>
                  <LaunchWindowsProvider>
                    <FilterProvider>
                      <GlobalStyle />

                      {auth.user.isLoggedIn() || isEmbeddedPurchases ? (
                        <LoggedRoutes />
                      ) : (
                        <UnloggedRoutes />
                      )}

                      {/** Start Modals */}
                      <WonModal />
                      <LostModal />
                      <AcceptedModal />
                      <RejectedModal />
                      <SingleOfferModal />
                      <BuyNowModal />
                      <SuccessModal />
                      <ConfirmMakeOfferModal />
                      <AutoBidOfferModal />
                      <MoreOptions />
                      <RemoveAutomaticOffer />
                      <TiebreakerRules />
                      <ConfirmAutomaticOffer />
                      <ConfirmBuyNowModal />
                      <PreferencesModal />
                      <Temperature.KnowMore />
                      <ProductNF />
                      <BuyFor />
                      <ConfirmBuyFor />
                      {/** End Modals */}

                      {/* <Onboarding /> */}
                      <ToastContainer />

                      {/** Start Tooltips */}
                      <TooltipFeedback />
                      <TooltipFeedbackOld />
                      {/** End Tooltips */}

                      {process.env.NODE_ENV !== 'test' && (
                        <React.Fragment>
                          <NotificationWrapper ref={notifier} />
                          <AuctionPusher
                            pusher={pusher}
                            notifier={notifier ? notifier.current : undefined}
                            auctions={auctions.allAuctions}
                          />
                        </React.Fragment>
                      )}
                    </FilterProvider>
                  </LaunchWindowsProvider>
                </AppProvider>
              </ThemeProvider>
              <ReactQueryDevtools initialIsOpen={false} />
            </QueryClientProvider>
          </FiltersContextProvider>
        </FlagProvider>
      </NotificationContextProvider>
    </ConfigCatContextProvider>
  );
};

export default App;
